import { AppThunk } from '../store/types';
import { TAudioWordNativeItem, WordsRest } from '../../common/rest/words/wordsRest';
import { mergeWordsLemmasAction, wordsLemmasPendingAction, setWordsDataAction, setWordsGeneratingAction, setWordsLoadFailedAction, setWordsGenerateFailedAction, setWordsNativeAction } from '../store/words/actions';
import { getAudioWords, getWordsLemmas, getWordsNative, isWordsNativeFor, sameWords } from '../store/words/selectors';
import { setSnackbarPanelAction } from '../store/general/actions';

// `${langCode}:${word}` already sent and not yet answered — so a tab remount doesn't resend them
const lemmasInFlight = new Set<string>();

export class WordsEffects {

	// Only asks for words that are neither cached in the store nor already requested.
	public static loadLemmas(langCode: string, words: string[]): AppThunk {
		return async (dispatch, getState) => {
			const known = getWordsLemmas(getState(), langCode);
			const toFetch = words.filter(w => !known[w] && !lemmasInFlight.has(`${langCode}:${w}`));
			if (!toFetch.length) return;
			toFetch.forEach(w => lemmasInFlight.add(`${langCode}:${w}`));
			dispatch(wordsLemmasPendingAction(langCode, 1));
			try {
				const lemmas = await WordsRest.fetchLemmas(langCode, toFetch);
				dispatch(mergeWordsLemmasAction(langCode, lemmas));
				// the server resolves a capped number of new words per request — ask again for the rest,
				// but only while it's making progress, so a failing AI can't loop forever
				const rest = toFetch.filter(w => !lemmas[w]);
				if (rest.length && rest.length < toFetch.length) {
					toFetch.forEach(w => lemmasInFlight.delete(`${langCode}:${w}`));
					await dispatch(WordsEffects.loadLemmas(langCode, rest));
				}
			} catch (e) {
				console.error('WordsEffects.loadLemmas failed', e);
			} finally {
				toFetch.forEach(w => lemmasInFlight.delete(`${langCode}:${w}`));
				dispatch(wordsLemmasPendingAction(langCode, -1));
			}
		}
	}

	public static loadNative(audioId: number, langCode: string): AppThunk {
		return async (dispatch, getState) => {
			const words = getAudioWords(getState(), audioId)?.words;
			if (!words?.length || isWordsNativeFor(getWordsNative(getState(), audioId), words, langCode)) return;
			dispatch(setWordsNativeAction(audioId, { langCode, words, items: null }));
			let items: TAudioWordNativeItem[] = [];
			try {
				const response = await WordsRest.fetchNative(audioId, langCode);
				// translated for a different list than the store's (regenerated meanwhile) — no tooltips
				if (sameWords(response.words || [], words)) items = response.items || [];
			} catch (e) {
				console.error('WordsEffects.loadNative failed', e);
			}
			// a newer request owns the slot — overwriting its loading mark would just trigger a repeat request
			if (!isWordsNativeFor(getWordsNative(getState(), audioId), words, langCode)) return;
			dispatch(setWordsNativeAction(audioId, { langCode, words, items }));
		}
	}

	// langCode: the viewer's native language — a stored translation comes along, so a reload shows it at once
	public static load(audioId: number, langCode?: string): AppThunk {
		return async (dispatch) => {
			try {
				dispatch(setWordsLoadFailedAction(audioId, false));
				const { native, ...data } = await WordsRest.fetch(audioId, langCode);
				// before the list, so the tab never sees the list without it and asks for it
				if (native && langCode) dispatch(setWordsNativeAction(audioId, { langCode, words: data.words, items: native }));
				dispatch(setWordsDataAction(audioId, data));
			} catch (e) {
				console.error('WordsEffects.load failed', e);
				dispatch(setWordsLoadFailedAction(audioId, true));
			}
		}
	}

	public static generate(audioId: number): AppThunk {
		return async (dispatch, getState) => {
			dispatch(setWordsGeneratingAction(audioId, true));
			dispatch(setWordsGenerateFailedAction(audioId, false));
			try {
				const { words } = await WordsRest.generate(audioId);
				// generating doesn't change who may regenerate — keep whatever the initial load found
				const canRegenerate = !!getAudioWords(getState(), audioId)?.canRegenerate;
				dispatch(setWordsDataAction(audioId, { words, canRegenerate }));
			} catch (e) {
				console.error('WordsEffects.generate failed', e);
				// no list yet: shown inline (with Try again); a failed regenerate keeps the old list, so snackbar
				if (getAudioWords(getState(), audioId)?.words.length) {
					dispatch(setSnackbarPanelAction(true, 'Failed to regenerate the word list'));
				} else {
					dispatch(setWordsGenerateFailedAction(audioId, true));
				}
			} finally {
				dispatch(setWordsGeneratingAction(audioId, false));
			}
		}
	}

}
